import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { type ApolloError, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'

import { setFormErrors } from 'Utilities'

import type {
  mutationEmployeeUpdate,
  mutationEmployeeUpdateVariables,
} from 'Constants/graphql/mutations/__generated__/mutationEmployeeUpdate'
import { MUTATION_EMPLOYEE_UPDATE } from 'Constants/graphql/mutations/MutationEmployeeUpdate'

import { useActiveUser } from 'Portal/src/hooks'
import { useStore } from 'Portal/src/stores'
import {
  cleanPhoneNumberWhenOnlyPrefix,
  createSelectOption,
  getSelectValue,
  validatePhoneNumber,
} from 'Portal/src/utilities/form/form.util'

import { editEmployeeFormEditEmployeeSchema } from '../EditEmployeeFormModal.schema'
import type {
  EditEmployeeFormCreateEmployeeSchema,
  EditEmployeeFormEditEmployeeSchema,
  EditEmployeeFormModalEditEmployeeProps,
} from '../EditEmployeeFormModal.types'
import { EditEmployeeFormModalForm } from './EditEmployeeFormModalForm'

export const EditEmployeeFormModalEditEmployee = (
  props: EditEmployeeFormModalEditEmployeeProps,
) => {
  const { employee, onHide, onSuccess } = props
  const { portalConfigStore } = useStore()
  const { portalConfig } = portalConfigStore
  const [submitError, setSubmitError] = useState<ApolloError | null>(null)

  const { activeOrganisation } = useActiveUser()
  const organisationID = activeOrganisation!.id
  const intl = useIntl()

  const formMethods = useForm<EditEmployeeFormEditEmployeeSchema>({
    defaultValues: {
      allowLogin: employee.allowLogin,
      costCenterID: createSelectOption({
        label: employee.costCenter?.name ?? '',
        value: employee.costCenter?.id?.toString() ?? '',
      }),
      email: employee.contact.email ?? null,
      externalReference: employee.externalReference ?? '',
      firstName: employee.contact.firstName ?? '',
      lastName: employee.contact.lastName ?? '',
      newCostCenter: null,
      officeID: createSelectOption({
        label: employee.office?.locationName ?? '',
        value: employee.office?.id?.toString() ?? '',
      }),
      phoneNumber: employee.contact.phoneNumber ?? '',
      rentCategoryTierID: createSelectOption({
        label:
          employee.organisation?.rentCategoryTiers?.rentCategoryTiers?.find(
            tier => tier.id === employee?.rentCategoryTierID || '',
          )?.name ?? '',
        value: employee.rentCategoryTierID?.toString() ?? '',
      }),
    },
    resolver: zodResolver(editEmployeeFormEditEmployeeSchema),
  })

  const [submitEditEmployee, { loading: isLoading }] = useMutation<
    mutationEmployeeUpdate,
    mutationEmployeeUpdateVariables
  >(MUTATION_EMPLOYEE_UPDATE, {
    onCompleted: data => {
      if (data.employeeUpdate.__typename === 'EmployeeUpdateProblem') {
        setFormErrors<EditEmployeeFormEditEmployeeSchema>(
          data,
          'employeeUpdate.invalidInputs',
          formMethods.setError,
        )

        return
      }

      if (
        formMethods.getValues('allowLogin') &&
        data.employeeUpdate.__typename === 'EmployeeUpdateSuccess' &&
        data.employeeUpdate.nonDirectUserExists
      ) {
        setSubmitError({
          message: intl.formatMessage({ id: 'PutEmployeeModal_user_exists' }),
        } as ApolloError)

        onSuccess(!!formMethods.getValues('newCostCenter'), false)

        return
      }

      onSuccess(!!formMethods.getValues('newCostCenter'))
    },
    onError: error => {
      setSubmitError(error)
    },
  })

  const handleSubmit = (
    data:
      | EditEmployeeFormCreateEmployeeSchema
      | EditEmployeeFormEditEmployeeSchema,
  ) => {
    if (data.phoneNumber) {
      data.phoneNumber = cleanPhoneNumberWhenOnlyPrefix(
        validatePhoneNumber(data.phoneNumber),
      )
    }

    const shouldAllowLoginFlagHaveValue =
      data.email && !portalConfig?.onlyAdminsCanLogin

    const allowLoginFlagValue = data.allowLogin ?? false

    submitEditEmployee({
      variables: {
        input: {
          ...data,
          allowLogin: shouldAllowLoginFlagHaveValue
            ? allowLoginFlagValue
            : undefined,
          costCenterID: getSelectValue(data.costCenterID) || null,
          email: data.email || null,
          id: employee.id.toString(),
          officeID: getSelectValue(data.officeID) || '0',
          organisationID,
          rentCategoryTierID: getSelectValue(data.rentCategoryTierID) || null,
        },
      },
    })
  }

  return (
    <EditEmployeeFormModalForm
      employee={employee}
      formMethods={formMethods as any}
      isSubmitting={isLoading}
      onHide={onHide}
      onRefetchEmployee={onSuccess}
      onSubmit={handleSubmit}
      organisationID={organisationID}
      submitError={submitError}
    />
  )
}
